import { useState } from "react";
import { CloudOff, CloudUpload, RefreshCw } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthContext";
import { EmptyState } from "../components/EmptyState";
import { FormModal } from "../components/FormModal";
import { PageHeader } from "../components/PageHeader";
import { removeOutboxItem, type OutboxInspection } from "../offline/outbox";
import { requestSync, useOfflineStatus, useOutboxItems } from "../offline/runtime";
import { retryNow } from "../offline/sync";
import { formatDate } from "../lib/format";

function statusOf(item: OutboxInspection, syncing: boolean) {
  if (item.status === "precisa_revisao") return { label: "Precisa de revisão", tone: "danger" };
  if (item.status === "enviando" && syncing) return { label: "Enviando…", tone: "info" };
  if (item.serverId) return { label: "Faltam fotos", tone: "warning" };
  return { label: "Aguardando envio", tone: "warning" };
}

export function PendingInspections() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { online, syncing } = useOfflineStatus();
  const { items, loading } = useOutboxItems(user?.id);
  const [discarding, setDiscarding] = useState<OutboxInspection | null>(null);
  const saved = (location.state as { saved?: "queued" | "partial" } | null)?.saved;

  const retry = async (item: OutboxInspection) => { await retryNow(item); if (user) void requestSync(user.id, queryClient); };
  const discard = async () => { if (!discarding) return; await removeOutboxItem(discarding.id); setDiscarding(null); };

  return <>
    <PageHeader
      eyebrow="Vistorias"
      title="Pendentes de envio"
      description="Vistorias salvas neste aparelho que ainda não chegaram ao servidor. Elas são enviadas sozinhas quando houver internet."
      actions={<button className="button secondary" disabled={!online || syncing || !items.length} onClick={() => user && void requestSync(user.id, queryClient)}><RefreshCw size={17}/> {syncing ? "Enviando…" : "Enviar agora"}</button>}
    />
    {saved && <div className="alert info" role="status">{saved === "partial"
      ? "A vistoria foi salva, mas algumas fotos ainda não subiram. Elas ficam guardadas neste aparelho e serão enviadas automaticamente."
      : online ? "Vistoria guardada neste aparelho. O envio começa agora." : "Vistoria guardada neste aparelho. Ela será enviada assim que a internet voltar. Não limpe os dados do navegador até lá."}</div>}
    {!online && <div className="alert info" role="status"><CloudOff size={15} aria-hidden="true"/> Sem conexão. O envio retoma sozinho quando a internet voltar.</div>}
    <section className="card pending-card">
      {loading ? <div className="loading">Carregando…</div> : !items.length
        ? <EmptyState title="Nada pendente" text="Todas as vistorias deste aparelho já foram enviadas." action={<CloudUpload/>}/>
        : <ul className="pending-list">{items.map((item) => {
          const status = statusOf(item, syncing);
          const canEdit = item.status === "precisa_revisao" && !item.serverId;
          return <li key={item.id} className="pending-item">
            <div className="pending-main">
              <div className="pending-title"><strong className="plate">{item.summary.plate || "Sem placa"}</strong><span className={`pending-status ${status.tone}`}>{status.label}</span></div>
              <p>{item.summary.companyName} · {item.summary.driverName}</p>
              <small>Vistoria de {formatDate(item.summary.inspectionDate)} · {item.photos.length} foto(s) a enviar{item.serverCode ? ` · ${item.serverCode}` : ""}{item.attempts ? ` · ${item.attempts} tentativa(s)` : ""}</small>
              {item.lastError && <p className="pending-error" role="note">{item.lastError}</p>}
            </div>
            <div className="pending-actions">
              {canEdit && <Link className="button primary" to={`/vistorias/nova?pendente=${encodeURIComponent(item.id)}`}>Revisar e corrigir</Link>}
              {(item.status !== "precisa_revisao" || item.serverId) && <button className="button secondary" disabled={!online || syncing} onClick={() => void retry(item)}>Tentar agora</button>}
              <button className="button ghost" onClick={() => setDiscarding(item)}>Descartar</button>
            </div>
          </li>;
        })}</ul>}
    </section>
    {discarding && <FormModal eyebrow="Pendente de envio" title="Descartar esta vistoria?" onClose={() => setDiscarding(null)} onSubmit={(event) => { event.preventDefault(); void discard(); }} submitLabel="Descartar definitivamente" submitTone="danger">
      <p className="inspection-delete-warning"><strong>A vistoria e as {discarding.photos.length} foto(s) guardadas neste aparelho serão apagadas e não poderão ser recuperadas.</strong>{discarding.serverCode ? ` A vistoria ${discarding.serverCode} já está no servidor; só as fotos pendentes serão perdidas.` : " Ela ainda não chegou ao servidor."}</p>
      <div className="inspection-delete-summary">
        <div><span>Veículo</span><strong>{discarding.summary.plate || "—"}</strong></div>
        <div><span>Empresa</span><strong>{discarding.summary.companyName}</strong></div>
        <div><span>Data</span><strong>{formatDate(discarding.summary.inspectionDate)}</strong></div>
      </div>
    </FormModal>}
  </>;
}
